import * as React from 'react';

import { Flex, Box } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Select } from '@lightspeed/flame/Select';
import clamp from 'lodash/clamp';

import { useKeyDown } from 'hooks/index';
import { LabelSize, SUPPORTED_LABELS } from 'label/utils/types';

import { useLabelPrintForm } from './hooks';
import { StyledFlex, StyledLabel, UnstyledButton } from './HubLabelPrintBody.style';
import { MAX_LABELS, MIN_LABELS } from './HubLabelPrintModal';
import { InputStepper } from './InputStepper';

const labelMarginsRange = {
  min: -20,
  max: 20,
};

export const HubLabelPrintBody: React.FC<{
  isLoading: boolean;
  isListOfLabels: boolean;
  labelPrintForm: ReturnType<typeof useLabelPrintForm>;
  submit: () => void;
  toggleContent: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ isLoading, isListOfLabels, labelPrintForm, submit, toggleContent }) => {
  const {
    register,
    handleSubmit,
    setValue,
    labelMargins,
    labelSize,
    formState,
    quantity,
  } = labelPrintForm;

  const { errors } = formState;
  const imageSize = labelSize || LabelSize.NORMAL;

  const editLabelMargin = (amount: number) => () => {
    if (!labelSize) {
      return;
    }
    const next = { ...labelMargins };
    if (!next[labelSize]) {
      next[labelSize] = amount;
    } else {
      next[labelSize] = clamp(
        next[labelSize] + amount,
        labelMarginsRange.min,
        labelMarginsRange.max
      );
    }
    setValue('labelMargins', next);
  };

  const editLabelQuantity = (amount: number) => () => {
    const next = { ...quantity };
    if (next.labels === undefined) {
      next.labels = amount + 1;
    } else {
      next.labels = clamp(next.labels + amount, MIN_LABELS, MAX_LABELS);
    }
    setValue('quantity', next);
  };

  useKeyDown('Enter', handleSubmit(submit));

  return (
    <>
      <Flex flexDirection="column" mb={3}>
        <StyledLabel>Current label</StyledLabel>
        <StyledFlex>
          <img
            style={{ alignSelf: 'center', maxHeight: '100%', maxWidth: '100%' }}
            data-test="print-label-image"
            className={`label-size-${imageSize}`}
            src={`https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/label/label-size-${imageSize}.svg`}
            alt={`${imageSize} labels`}
          />
        </StyledFlex>
      </Flex>
      <Flex flexDirection="column">
        <Box mb={3}>
          <StyledLabel>Select label size</StyledLabel>
          <Select
            data-test="print-label-select-size"
            {...register('labelSize', { required: true })}
          >
            {[{ label: 'Select', value: '', dimensions: '' }, ...SUPPORTED_LABELS].map(
              ({ label, value, dimensions }) => (
                <option data-test={`print-label-select-size-${value}`} value={value} key={value}>
                  {label} {dimensions ? ` - ${dimensions}` : null}
                </option>
              )
            )}
          </Select>
        </Box>
        <Box mb={3}>
          <InputStepper
            labelText="Select number of labels"
            descriptionText={`Make sure that the label printer is loaded with enough ${labelSize} labels`}
            isInputDisabled={isLoading || isListOfLabels || !labelSize}
            inputRef={'quantity.labels' as const}
            isIncrementButtonDisabled={
              !labelSize || quantity.labels >= MAX_LABELS || isListOfLabels
            }
            isDecrementButtonDisabled={
              !labelSize || quantity.labels <= MIN_LABELS || isListOfLabels
            }
            minInputValue={MIN_LABELS}
            maxInputValue={MAX_LABELS}
            status={errors.quantity ? 'error' : undefined}
            stepperFunction={editLabelQuantity}
            printForm={labelPrintForm}
            placeholder="1"
          />
        </Box>
        <Box mb={3}>
          <InputStepper
            labelText="Adjust label margins"
            descriptionText={`Label margins can be between ${labelMarginsRange.min} to ${labelMarginsRange.max}`}
            isInputDisabled={isLoading || !labelSize}
            inputRef={labelSize ? (`labelMargins.${labelSize}` as const) : 'default'}
            isIncrementButtonDisabled={
              !labelSize || labelMargins[labelSize] >= labelMarginsRange.max
            }
            isDecrementButtonDisabled={
              !labelSize || labelMargins[labelSize] <= labelMarginsRange.min
            }
            status={errors.labelMargins ? 'error' : undefined}
            stepperFunction={editLabelMargin}
            printForm={labelPrintForm}
            maxInputValue={labelMarginsRange.max}
            minInputValue={labelMarginsRange.min}
          />
        </Box>
      </Flex>
      <Divider />
      <Flex mt={3}>
        <UnstyledButton data-test="advanced-settings-button" onClick={() => toggleContent(true)}>
          Advanced settings
        </UnstyledButton>
      </Flex>
    </>
  );
};
